import PropTypes from "prop-types";
import Button from "@shared/v2/Button";
import Portal from "@shared/v1/Portal";
import { BullseyeArrowRegularV6, BullseyeArrowSolidV6, XSolidV6 } from "@shared/v2/Icomoon";
import IconButton from "@shared/v2/IconButton";
import Tooltip from "@shared/v2/Tooltip";
import { useTheme } from "../../../reducers/layoutReducer/selectors";
import ProgressRing from "./ProgressRing";
import DailyProgress from "./DailyProgress";
import HelpPopover from "./HelpPopover";
import DitlCardList from "./DitlCardList";
import useDayInTheLife from "./useDayInTheLife";
import { toDitlCards } from "./ditlCards";

const DayInTheLife = ({ open: showDayInTheLife, onOpenChange }) => {
  const isPlace = useTheme()?.name === "place-theme";
  const { sections, loading, setSectionCompleted } = useDayInTheLife({ enabled: showDayInTheLife });
  const ditlCards = toDitlCards(sections);

  const totalDitlCardsCount = ditlCards.length;
  const completedDitlCards = ditlCards.filter((t) => t.completed);
  const completedDitlCardsCount = completedDitlCards.length;
  const completedPercentage =
    totalDitlCardsCount > 0 ? (completedDitlCardsCount / totalDitlCardsCount) * 100 : 0;

  const toggleDayInTheLife = () => onOpenChange(!showDayInTheLife);

  return (
    <>
      <Tooltip
        className="tw-flex"
        placement="bottom"
        content="Day in the Life"
        innerClassName="!tw-bg-neutral-gray-75"
        arrowClassName="!tw-fill-neutral-gray-75"
        trigger={
          <Button
            className={`tw-group tw-w-[36px] tw-h-[36px] tw-text-14d !tw-border-1px !tw-border-solid tw-flex tw-items-center tw-justify-center tw-ease-out tw-transition-all hover:tw-duration-150 active:tw-duration-50
                        ${
                          isPlace
                            ? `tw-bg-white !tw-border-gray-75 tw-bg-opacity-10 hover:!tw-bg-opacity-20 active:!tw-bg-white active:!tw-bg-opacity-30 ${showDayInTheLife ? "!tw-border-[var(--brand-light)]" : ""}`
                            : `!tw-text-[var(--neutral-gray-75)] active:!tw-bg-[var(--brivity-blue-20)] ${
                                showDayInTheLife
                                  ? "!tw-bg-[var(--brivity-blue-20)] !tw-border-[var(--brivity-blue-20)]"
                                  : "!tw-bg-white !tw-border-[var(--neutral-gray-30)] hover:!tw-bg-[var(--brand-lightest)]"
                              }`
                        }`}
            aria-expanded={showDayInTheLife}
            onClick={toggleDayInTheLife}
          >
            {showDayInTheLife ? (
              <BullseyeArrowSolidV6 className="tw-h-[16px] tw-w-[16px]" />
            ) : (
              <>
                <BullseyeArrowRegularV6 className="tw-h-[16px] tw-w-[16px] group-hover:tw-hidden group-active:tw-hidden" />
                <BullseyeArrowSolidV6 className="tw-h-[16px] tw-w-[16px] tw-hidden group-hover:tw-block group-active:tw-block" />
              </>
            )}
            <ProgressRing
              className="tw-absolute tw-inset-[-1px]"
              percentage={completedPercentage}
              size={36}
              strokeWidth={3.6}
              color="var(--semantic-yellow-100, #FFA826)"
            />
          </Button>
        }
      />
      <Portal portalId="day-in-the-life-portal">
        <div
          className={`tw-max-w-[100vw] tw-h-full tw-w-[564px] tw-absolute tw-z-[100] tw-shadow-[0px_6px_20px_2px_#33333329] tw-bg-[#F5F5F5] tw-flex tw-flex-col tw-transform tw-transition-transform tw-duration-[500ms] tw-ease-in-out tw-right-0 tw-pointer-events-auto ${
            showDayInTheLife ? "tw-translate-x-0" : "tw-translate-x-full"
          }`}
        >
          <div className="tw-flex tw-bg-white tw-items-center tw-justify-between tw-pl-[24px] tw-pr-[12px] tw-py-[12px] tw-border-solid tw-border-x-0 tw-border-t-0 tw-border-b-[1px] tw-border-[#EBEBEB]">
            <h3 className="tw-text-[18px] tw-m-0">Day in the Life</h3>
            <div className="tw-flex tw-gap-[10px]">
              <HelpPopover />
              <IconButton schema="transparent" onClick={toggleDayInTheLife}>
                <XSolidV6 className="tw-w-[12px] tw-h-[12px]" />
              </IconButton>
            </div>
          </div>
          <DailyProgress
            completedDitlCards={completedDitlCards}
            completedPercentage={completedPercentage}
            drawerOpen={showDayInTheLife}
            onUndo={(id) => setSectionCompleted(id, false)}
          />
          <DitlCardList
            loading={loading && !sections.length}
            cards={ditlCards}
            onCardDone={(id) => setSectionCompleted(id, true)}
          />
        </div>
      </Portal>
    </>
  );
};

DayInTheLife.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
};

export default DayInTheLife;
